export class ApiError extends Error {
    status;
    code;
    issues;
    name = "ApiError";
    constructor(status, code, message, issues = []) {
        super(message);
        this.status = status;
        this.code = code;
        this.issues = issues;
    }
}
/** 401 codes that mean "your login token is no longer valid" (as opposed to e.g. a wrong 2FA code). */
const SESSION_ERRORS = new Set(["UNAUTHORIZED", "ADMIN_UNAUTHORIZED"]);
/** Refresh a little before the real expiry so a request never races the deadline. */
const EXPIRY_MARGIN_MS = 30_000;
export class ApiClient {
    opts;
    accessToken = null;
    accessExpiresAt = 0;
    refreshing = null;
    fetchImpl;
    now;
    listeners = new Set();
    authPaths;
    constructor(opts) {
        this.opts = opts;
        if (opts.transport === "body" && !opts.refreshStore) {
            throw new Error('The "body" transport needs a refreshStore');
        }
        this.fetchImpl = opts.fetch ?? ((...args) => globalThis.fetch(...args));
        this.now = opts.now ?? Date.now;
        this.authPaths = opts.authPaths ?? { refresh: "/auth/refresh", logout: "/auth/logout" };
    }
    // ───────────────────────── Session state ─────────────────────────
    get isLoggedIn() {
        return this.accessToken !== null;
    }
    /** Listen for login / logout changes. Returns an unsubscribe function. */
    onAuthChange(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    async acceptTokens(tokens) {
        const wasLoggedIn = this.isLoggedIn;
        this.accessToken = tokens.accessToken;
        this.accessExpiresAt = Date.parse(tokens.accessTokenExpiresAt);
        if (this.opts.transport === "body" && tokens.refreshToken)
            await this.opts.refreshStore.set(tokens.refreshToken);
        if (!wasLoggedIn)
            this.listeners.forEach((l) => l(true));
    }
    async dropSession(notify) {
        const wasLoggedIn = this.isLoggedIn;
        this.accessToken = null;
        this.accessExpiresAt = 0;
        if (this.opts.transport === "body")
            await this.opts.refreshStore.clear();
        if (wasLoggedIn)
            this.listeners.forEach((l) => l(false));
        if (notify && wasLoggedIn)
            this.opts.onSessionExpired?.();
    }
    /** Try to resume a session after a page reload (uses the refresh cookie / stored token). */
    async restoreSession() {
        return this.refresh();
    }
    refresh() {
        this.refreshing ??= this.doRefresh().finally(() => {
            this.refreshing = null;
        });
        return this.refreshing;
    }
    async doRefresh() {
        let body = {};
        if (this.opts.transport === "body") {
            const token = await this.opts.refreshStore.get();
            if (!token)
                return false;
            body = { refreshToken: token };
        }
        try {
            const tokens = await this.raw(this.authPaths.refresh, {
                method: "POST",
                body,
                auth: false,
            });
            await this.acceptTokens(tokens);
            return true;
        }
        catch (error) {
            if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
                await this.dropSession(true);
                return false;
            }
            throw error;
        }
    }
    // ───────────────────────── Transport ─────────────────────────
    headers(withBody) {
        const h = {};
        if (withBody)
            h["content-type"] = "application/json";
        if (this.opts.transport === "cookie")
            h["x-custos-client"] = this.opts.clientName ?? "web";
        return h;
    }
    url(path, query) {
        const base = this.opts.baseUrl.replace(/\/$/, "");
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query ?? {}))
            if (v !== undefined)
                params.set(k, String(v));
        const qs = params.toString();
        return `${base}${path}${qs ? `?${qs}` : ""}`;
    }
    /** One HTTP call, no retry logic. */
    async raw(path, options, accessToken) {
        const method = options.method ?? "GET";
        const hasBody = options.body !== undefined;
        const headers = this.headers(hasBody);
        if (accessToken)
            headers.authorization = `Bearer ${accessToken}`;
        let res;
        try {
            res = await this.fetchImpl(this.url(path, options.query), {
                method,
                headers,
                body: hasBody ? JSON.stringify(options.body) : null,
                credentials: this.opts.transport === "cookie" ? "include" : "omit",
            });
        }
        catch {
            throw new ApiError(0, "NETWORK_ERROR", "Can't reach the server. Check your connection and try again.");
        }
        const text = await res.text();
        let data = null;
        if (text) {
            try {
                data = JSON.parse(text);
            }
            catch {
                data = null;
            }
        }
        if (!res.ok) {
            const err = data?.error;
            throw new ApiError(res.status, err?.code ?? "HTTP_ERROR", err?.message ?? `Request failed (${res.status})`, err?.issues ?? []);
        }
        return data;
    }
    async request(path, options = {}) {
        if (options.auth === false)
            return this.raw(path, options);
        if (!this.accessToken || this.now() >= this.accessExpiresAt - EXPIRY_MARGIN_MS) {
            if (!(await this.refresh()))
                throw new ApiError(401, "UNAUTHORIZED", "Not logged in");
        }
        try {
            return await this.raw(path, options, this.accessToken);
        }
        catch (error) {
            // Only a rejected LOGIN token is worth a refresh + retry. Other 401s (e.g. a wrong 2FA code)
            // must not be re-sent: that would count as a second failed attempt.
            if (!(error instanceof ApiError) || error.status !== 401 || !SESSION_ERRORS.has(error.code))
                throw error;
            // Access token rejected (e.g. server restarted with a new key) — refresh once and retry.
            if (!(await this.refresh()))
                throw error;
            return this.raw(path, options, this.accessToken);
        }
    }
    /** A binary file (chat image) with the access token; the caller turns it into a blob: URL. */
    async requestBlob(path) {
        if (!this.accessToken || this.now() >= this.accessExpiresAt - EXPIRY_MARGIN_MS) {
            if (!(await this.refresh()))
                throw new ApiError(401, "UNAUTHORIZED", "Not logged in");
        }
        const headers = this.headers(false);
        headers.authorization = `Bearer ${this.accessToken}`;
        let res;
        try {
            res = await this.fetchImpl(this.url(path), {
                headers,
                credentials: this.opts.transport === "cookie" ? "include" : "omit",
            });
        }
        catch {
            throw new ApiError(0, "NETWORK_ERROR", "Can't reach the server. Check your connection and try again.");
        }
        if (!res.ok)
            throw new ApiError(res.status, "HTTP_ERROR", `Request failed (${res.status})`);
        return res.blob();
    }
    // ───────────────────────── Public / auth ─────────────────────────
    config() {
        return this.raw("/config", {});
    }
    // ── System messages (D78) ──
    /** Login page: no session needed. */
    async publicMessages() {
        return (await this.raw("/messages/public", {})).messages;
    }
    async messages() {
        return (await this.request("/messages")).messages;
    }
    // ── Live support chat (D79) ──
    chat() {
        return this.request("/support/chat");
    }
    async chatUnread() {
        return (await this.request("/support/unread")).unread;
    }
    /** image = base64 PNG/JPEG. confirmedTxId: the sender confirmed a possible key is a transaction ID. */
    sendChat(input) {
        return this.request("/support/chat/messages", { method: "POST", body: input });
    }
    markChatRead() {
        return this.request("/support/chat/read", { method: "POST", body: {} });
    }
    chatImage(id) {
        return this.requestBlob(`/support/attachments/${encodeURIComponent(id)}`);
    }
    dismissMessage(id) {
        return this.request(`/messages/${encodeURIComponent(id)}/dismiss`, { method: "POST" });
    }
    register(email, password) {
        return this.raw("/auth/register", {
            method: "POST",
            body: { email, password },
        });
    }
    verifyEmail(token) {
        return this.raw("/auth/verify-email", { method: "POST", body: { token } });
    }
    resendVerification(email) {
        return this.raw("/auth/resend-verification", { method: "POST", body: { email } });
    }
    login(email, password) {
        return this.raw("/auth/login", { method: "POST", body: { email, password } });
    }
    totpSetup(challengeToken) {
        return this.raw("/auth/totp/setup", { method: "POST", body: { challengeToken } });
    }
    async totpEnable(challengeToken, code) {
        const tokens = await this.raw("/auth/totp/enable", {
            method: "POST",
            body: { challengeToken, code },
        });
        await this.acceptTokens(tokens);
    }
    async loginTotp(challengeToken, code) {
        const tokens = await this.raw("/auth/login/totp", {
            method: "POST",
            body: { challengeToken, code },
        });
        await this.acceptTokens(tokens);
    }
    async logout() {
        try {
            if (this.accessToken)
                await this.raw(this.authPaths.logout, { method: "POST" }, this.accessToken);
        }
        catch {
            // Logging out locally must always work, even if the server can't be reached.
        }
        await this.dropSession(false);
    }
    forgotPassword(email) {
        return this.raw("/auth/password/forgot", {
            method: "POST",
            body: { email },
        });
    }
    resetPassword(token, newPassword, totpCode) {
        return this.raw("/auth/password/reset", {
            method: "POST",
            body: totpCode ? { token, newPassword, totpCode } : { token, newPassword },
        });
    }
    confirmWithdrawalEmail(token) {
        return this.raw("/withdrawals/confirm-email", { method: "POST", body: { token } });
    }
    // ───────────────────────── Account ─────────────────────────
    me() {
        return this.request("/me");
    }
    async sessions() {
        return (await this.request("/auth/sessions")).sessions;
    }
    revokeSession(id) {
        return this.request(`/auth/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    revokeOtherSessions() {
        return this.request("/auth/sessions/revoke-others", { method: "POST" });
    }
    async balances() {
        return (await this.request("/balances")).balances;
    }
    async deposits(limit) {
        return (await this.request("/deposits", { query: { limit } })).deposits;
    }
    async depositAddresses() {
        return (await this.request("/deposit-addresses")).addresses;
    }
    /** Returns the address for this family, creating it on first use. */
    depositAddress(family) {
        return this.request(`/deposit-addresses/${family}`, { method: "POST" });
    }
    // ───────────────────────── Whitelist & withdrawals ─────────────────────────
    async whitelist() {
        return (await this.request("/whitelist")).addresses;
    }
    addWhitelist(input) {
        return this.request("/whitelist", { method: "POST", body: input });
    }
    removeWhitelist(id) {
        return this.request(`/whitelist/${encodeURIComponent(id)}`, { method: "DELETE" });
    }
    async withdrawals() {
        return (await this.request("/withdrawals")).withdrawals;
    }
    withdrawal(id) {
        return this.request(`/withdrawals/${encodeURIComponent(id)}`);
    }
    requestWithdrawal(input) {
        return this.request("/withdrawals", { method: "POST", body: input });
    }
    cancelWithdrawal(id) {
        return this.request(`/withdrawals/${encodeURIComponent(id)}/cancel`, { method: "POST" });
    }
    // ───────────────────────── Prices, portfolio & swaps (Phase 8) ─────────────────────────
    async prices() {
        return (await this.request("/prices")).prices;
    }
    portfolio(currency) {
        return this.request("/portfolio", { query: { currency } });
    }
    portfolioHistory(currency, range) {
        return this.request("/portfolio/history", { query: { currency, range } });
    }
    swapOptions() {
        return this.request("/swaps/options");
    }
    swapQuote(input) {
        return this.request("/swaps/quote", { method: "POST", body: input });
    }
    /** Confirms a quote. Retrying with the same idempotencyKey is safe (returns the same swap). */
    confirmSwap(quoteId, idempotencyKey) {
        return this.request("/swaps", { method: "POST", body: { quoteId, idempotencyKey } });
    }
    async swaps() {
        return (await this.request("/swaps")).swaps;
    }
    // ───────────────────────── Self-custody vault (Phase 9) ─────────────────────────
    /** On-chain balances of the vault's PUBLIC addresses. Never pass anything secret here. */
    vaultBalances(addresses, currency) {
        return this.request("/vault/balances", { method: "POST", body: { addresses, currency } });
    }
    /** Network details for a transfer from a vault address. Amount in base units. Public data only. */
    vaultPrepareSend(input) {
        return this.request("/vault/send/prepare", { method: "POST", body: input });
    }
    /** Relays a transaction that was signed ON THE DEVICE. */
    vaultBroadcast(input) {
        return this.request("/vault/send/broadcast", { method: "POST", body: input });
    }
    // ── Vault recovery (Phase 9c): ciphertext only ──
    vaultRecovery() {
        return this.request("/vault/recovery");
    }
    /** Saves the backup SEALED ON THE DEVICE for the owner's recovery key, plus public addresses. */
    vaultSaveBackup(input) {
        return this.request("/vault/recovery/backup", { method: "PUT", body: input });
    }
    vaultDisableRecovery() {
        return this.request("/vault/recovery/backup", { method: "DELETE" });
    }
    vaultRequestRecovery(input) {
        return this.request("/vault/recovery/request", { method: "POST", body: input });
    }
    vaultCancelRecovery() {
        return this.request("/vault/recovery/cancel", { method: "POST" });
    }
    vaultRecoveryBox(requestId) {
        return this.request(`/vault/recovery/request/${encodeURIComponent(requestId)}/box`);
    }
    vaultCompleteRecovery(requestId) {
        return this.request(`/vault/recovery/request/${encodeURIComponent(requestId)}/complete`, { method: "POST" });
    }
    vaultTxStatus(chain, txHash) {
        return this.request(`/vault/tx/${encodeURIComponent(chain)}/${encodeURIComponent(txHash)}`);
    }
}
export function createApiClient(opts) {
    return new ApiClient(opts);
}
//# sourceMappingURL=client.js.map